import React, { useState, useEffect } from 'react';
import {
	ProcessWorkflowState,
	RequestState,
	ProcessModel,
	ProcessType,
	ProcessQueue,
	ProcessState,
	ProcessRequestStatus,
	CreateProcessRequest
} from '../../../../../Services/WorkflowServicesTypes';
import { useWorkFlowContext } from '../context/WorkflowProcessContext';
import { formatLabel } from '../utils';
import Btn from '../../../../../Components/Btn';
import EmptyState from '../../../../../Components/EmptyState';
import Checkbox from '../../../../../Components/Checkbox';
import { Article, ArticleFoldable } from '../../../../../Components/Article/Article';
import Picto from '../../../../../Components/Picto';
import SectionMessage from '../../../../../Components/SectionMessage';
import { List, ListItem} from '../../../../../Components/List';
import { Switch } from '../../../../../Components/Checkbox/Checkbox';
import styles from './WorkflowProcess.module.css';
import { dateUtils } from '../../../../../Util/DateUtil';

const { humanizedDurationFromNow } = dateUtils();

export const WorkflowProcessContainer = () => {

	const {
		workflowProcessItems,
		sendWorkflowRequests,
		isSyncing
	} = useWorkFlowContext();
	
	const [selectedProcesses, setSelectedProcesses] = useState<ProcessQueue[]>([]);
	const [isMultipleSelectionEnabled, setIsMultipleSelectionEnabled] = useState(false);

	useEffect(() => {
		if (!isMultipleSelectionEnabled) {
			setSelectedProcesses([]);
		}
	}, [isMultipleSelectionEnabled]);

	if (!workflowProcessItems || workflowProcessItems.length === 0) {
		return (
			<div className="empty-state">
				<EmptyState
					title={'No process found'}
					text={'There are currently no items in the workflow. Please check back later.'}
					icon={'far fa-star'}
					verticalSize='s'
				/>
			</div>
		);
	}

	// const handleLaunchSelectedProcessTypes = () => {
	// 	const requests = selectedProcessTypes.map(processType => ({
	// 		processType: processType,
	// 		processIdentifier: '',
	// 		force: false,
	// 	}));
	// 	sendWorkflowRequests(requests);
	// 	setProcessByIdentifiersPanelIsOpen(false);
	// };

	// const handleRefreshByItemsSelected = (type: string, process: Array<string>) => {
	// 	const requestApis = process.map((state) => ({
	// 		processType: type,
	// 		processIdentifier: state,
	// 		force: false,
	// 	}));
	// 	sendWorkflowRequests(requestApis);
	// 	setProcessByIdentifiersPanelIsOpen(false);
	// };


	const handleSyncSelectedProcesses = () => {
		if (isSyncing) return;

		const request:CreateProcessRequest[] = selectedProcesses.map((p) => ({
			processType: p.processType,
			processIdentifier: p.processIdentifier,
			force: false,
		}));
		sendWorkflowRequests(request, () => {
			setIsMultipleSelectionEnabled(false);
		});
	};

	const handleSelectProcess = (processType: ProcessType, processIdentifier = '') => {
		if (processIdentifier) {
			const alreadySelected = selectedProcesses.some(p => p.processIdentifier === processIdentifier);
			if (alreadySelected) {
				setSelectedProcesses(p => p.filter(p => p.processIdentifier != processIdentifier));
			} else {
				setSelectedProcesses(p => [...p, {processType: processType, processIdentifier: processIdentifier}]);
			}
			return;
		}

		const alreadySelected = selectedProcesses.some(p => {
			return !p.processIdentifier && p.processType === processType;
		});

		const tmpSelectedProcesses = selectedProcesses.filter(p => p.processType != processType);
		if (!alreadySelected) {
			tmpSelectedProcesses.push({
				processType: processType,
				processIdentifier: processIdentifier
			});
		}

		setSelectedProcesses(tmpSelectedProcesses);
	};

	return (
		<>
			<div className='flex mb_15'>
				<div className='flex_item_full'>
					<div className='h2'>Workflow process</div>
				</div>
				<div className='flex_item_fix ml_30'>
					{isMultipleSelectionEnabled &&
						<Btn
							message={'Sync ' + selectedProcesses.length + ' processes'}
							disabled={selectedProcesses.length === 0}
							onClick={handleSyncSelectedProcesses}
							isLoading={isSyncing}
						/>
					}
					<Btn
						onClick={() => setIsMultipleSelectionEnabled(!isMultipleSelectionEnabled)}
						style="ghost"
						color="secondary"
					>
						<Switch
							label={'Multiple selection'}
							size="xs"
							forcedChecked={isMultipleSelectionEnabled}
							align="center"
							position="right"
						/>
					</Btn>
				</div>
			</div>
			<div>
				{workflowProcessItems.map((process: ProcessWorkflowState, i) => (
					<Process
						key={process.processType + '_' + i}
						level={1}
						type={process.processType}
						label={formatLabel(process.processType)}
						status={process.mainState.processState}
						date={process.mainState.stateDateUtc}
						message={process.mainState.message}
						subProcesses={process.stateWithIdentifiers}
						requests={process.mainState.requests}
						isMultipleSelectionEnabled={isMultipleSelectionEnabled}
						selectedProcesses={selectedProcesses}
						handleSelectProcess={handleSelectProcess}
					/>
				))}
			</div>
		</>
	);
};

interface IProcess {
	level: number;
	type: ProcessType;
	subProcessId?: string;
	label?: string;
	status: string;
	date?: string | null;
	message?: string;
	subProcesses?: ProcessModel[];
	requests?: RequestState[];
	isMultipleSelectionEnabled?: boolean;
	selectedProcesses?: ProcessQueue[];
	handleSelectProcess: any;
}

function Process({
	level,
	type,
	subProcessId = '',
	label,
	status,
	date,
	message,
	subProcesses = [],
	requests = [],
	isMultipleSelectionEnabled = false,
	selectedProcesses = [],
	handleSelectProcess
}:IProcess):JSX.Element {

	const [messageIsOpen, setMessageIsOpen] = useState(false);
	const [isMultiSelected, setIsMultiSelected] = useState(false);
	const [isMultiDisabled, setIsMultiDisabled] = useState(false);
	const hasSubProcesses = subProcesses && subProcesses.length > 0;
	const { sendWorkflowRequests } = useWorkFlowContext();
	const pendingRequests = requests.filter(x => x.requestStatus === ProcessRequestStatus.Pending);

	useEffect(() => {
		const typeIsSelected = selectedProcesses.some(p => {
			return p.processIdentifier === '' && p.processType === type;
		});
		const subProcessIsSelected = !!subProcessId && selectedProcesses.some(p => {
			return p.processIdentifier === subProcessId && p.processType === type;
		});
		setIsMultiDisabled(!!subProcessId && (typeIsSelected));
		setIsMultiSelected(typeIsSelected || subProcessIsSelected);
	}, [selectedProcesses]);

	const getSyncIsDisabled = (status) => {
		if (pendingRequests.length > 0) return true;
		switch (status) {
			case ProcessState.TerminatedWithError:
			case ProcessState.TerminatedWithNoOp:
			case ProcessState.TerminatedWithSuccess:
				return false;
			default: return true;
		}
	};

	const getStatusLabel = (status) => {
		switch (status) {
			case ProcessState.OnGoing:
				return 'Sync in progress';
			case ProcessState.Pending:
				return 'Sync pending';
			case ProcessState.TerminatedWithError:
				return 'Terminated with error';
			case ProcessState.TerminatedWithNoOp:
			case ProcessState.TerminatedWithSuccess:
				return 'Synchronized with success';
			default:
				return null;
		}
	};

	const getStatusPicto = (status) => {
		switch (status) {
			case ProcessState.OnGoing:
				return (
					<Picto
						size="xs"
						color="blue"
						icon="fas fa-sync-alt"
					/>
				);
			case ProcessState.Pending:
				return (
					<Picto
						size="xs"
						color="blue"
						icon="fas fa-hourglass-half"
					/>
				);
			case ProcessState.TerminatedWithError:
				return (
					<Picto
						size="xs"
						color="red"
						icon="fas fa-times"
					/>
				);
			case ProcessState.TerminatedWithNoOp:
			case ProcessState.TerminatedWithSuccess:
				return (
					<Picto
						size="xs"
						color="green"
						icon="fas fa-check"
					/>
				);
			default:
				return null;
		}
	};

	const handleSyncProcess = () => {
		const newRequests = [
			{
				processType: type,
				processIdentifier: subProcessId,
				force: false,
			}
		];
		sendWorkflowRequests(newRequests);
	};

	const inner = (
		<>
			<div className={styles.row}>
				<div className={styles.colLabel}>
					<div className={styles.label}>
						{hasSubProcesses &&
							<div className={styles.labelFold}>
								<Btn
									icon={messageIsOpen ? 'fas fa-sm fa-chevron-up s_12' : 'fas fa-sm fa-chevron-down s_12'}
									color="secondary"
									style="outline"
									onClick={() => setMessageIsOpen(!messageIsOpen)}
									className={styles.foldBtn}
									tooltip="Toggle sub processes"
								/>
							</div>
						}
						<div className={styles.labelInfos}>
							<div className={level === 1 ? styles.mainLabelText : styles.labelText}>
								{label}
							</div>
							{pendingRequests.length > 0 &&
								<div>
									{pendingRequests.length} pending request
								</div>
							}
						</div>
					</div>
				</div>
				<div className={styles.colStatus}>
					<div className={styles.status}>
						<div className={styles.statusPicto}>{getStatusPicto(status)}</div>
						<div className={styles.statusLabel}>{getStatusLabel(status)}</div>
					</div>
				</div>
				<div className={styles.colDate}>
					<div className={styles.date}>
						{humanizedDurationFromNow(date, true)}
					</div>
				</div>
				<div className={styles.colAction}>
					{isMultipleSelectionEnabled ?
						<Checkbox
							forcedChecked={isMultiSelected}
							onChange={() => handleSelectProcess(type, subProcessId)}
							disabled={getSyncIsDisabled(status) || isMultiDisabled}
						/>
						:
						<Btn
							icon="fas fa-sync-alt"
							message="Sync"
							style="reverse"
							size="s"
							onClick={handleSyncProcess}
							disabled={getSyncIsDisabled(status)}
						/>
					}
				</div>
			</div>
			{message &&
				<div className={styles.message}>
					<SectionMessage
						appearance="background_outline"
						type="log"
						showIcon={false}
						text={message}
						fontSize="s"
						noMargin={true}
					/>
				</div>
			}
			{pendingRequests.length > 0 &&
				<div className={styles.requestsList}>
					<Article hasMargin={false} innerSize='s'>
						{pendingRequests.map((request, i) => 
							<div className={styles.row} key={request.requestDateUtc + '_' + i}>
								<div className={styles.colLabel}>
									<div className={styles.status}>
										<div className={styles.statusPicto}>
											<Picto
												size="xxs"
												color="blue"
												icon="fas fa-arrow-right"
											/>
										</div>
										<div className={styles.statusLabel}>Upcoming request</div>
									</div>
								</div>
								<div className={styles.colStatus}>{request.originatorUserEmail}</div>
								<div className={styles.colDate}>
									<div className={styles.date}>
										{humanizedDurationFromNow(request.requestDateUtc, true)}
									</div>
								</div>
								<div className={styles.colAction}></div>
							</div>
						)}
					</Article>
				</div>
			}
			{hasSubProcesses && messageIsOpen &&
				<div className={styles.subProcessesList}>
					<List removeVerticalMargin={true}>
						{subProcesses.map((subProcess, i) => 
							<ListItem key={subProcess.processIdentifier + '_' + i}>
								<Process
									level={2}
									type={type}
									subProcessId={subProcess.processIdentifier}
									label={subProcess.processIdentifier}
									status={subProcess.processState}
									date={subProcess.stateDateUtc}
									message={subProcess.message}
									requests={subProcess.requests}
									isMultipleSelectionEnabled={isMultipleSelectionEnabled}
									selectedProcesses={selectedProcesses}
									handleSelectProcess={handleSelectProcess}
								/>
							</ListItem>
						)}
					</List>
				</div>
			}
		</>
	);

	if (level === 1) {
		return (
			<ArticleFoldable isOpen={hasSubProcesses && messageIsOpen} innerSize='m'>
				{inner}
			</ArticleFoldable>
		);
	}

	return inner;
}